import React, { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'react-toastify';
import api from '../services/api';

const AppManagement = () => {
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadForm, setUploadForm] = useState({
    version_name: '',
    version_code: '',
    release_notes: '',
    is_required: false,
    apk: null
  });
  const fileInputRef = useRef(null);

  // Hero Image State
  const [heroImages, setHeroImages] = useState({ office: null, driver: null, all: null });
  const [heroLoading, setHeroLoading] = useState(false);
  const [heroUploading, setHeroUploading] = useState(false);
  const heroFileRef = useRef(null);
  const [selectedHeroType, setSelectedHeroType] = useState('all');

  // Logo State
  const [logos, setLogos] = useState({ header: null, footer: null });
  const [logoLoading, setLogoLoading] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);
  const logoFileRef = useRef(null);
  const [selectedLogoType, setSelectedLogoType] = useState('header');

  const fetchVersions = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/app/versions');
      setVersions(response.data || []);
    } catch (err) {
      console.error('Error fetching versions:', err);
      toast.error('Failed to load app versions');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchHeroImages = useCallback(async () => {
    try {
      setHeroLoading(true);
      const response = await api.get('/cms/heroes');
      const heroes = response.data || [];
      const heroMap = {};
      heroes.forEach(h => {
        if (h.page_id === 'mobile-office') heroMap.office = h.image_url;
        else if (h.page_id === 'mobile-driver') heroMap.driver = h.image_url;
        else if (h.page_id === 'mobile-app') heroMap.all = h.image_url;
      });
      setHeroImages(heroMap);
    } catch (err) {
      console.error('Error fetching hero images:', err);
    } finally {
      setHeroLoading(false);
    }
  }, []);

  const fetchLogos = useCallback(async () => {
    try {
      setLogoLoading(true);
      const response = await api.get('/cms/mobile-logos');
      setLogos(response.data || { header: null, footer: null });
    } catch (err) {
      console.error('Error fetching logos:', err);
    } finally {
      setLogoLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchVersions();
    fetchHeroImages();
    fetchLogos();
  }, [fetchVersions, fetchHeroImages, fetchLogos]);

  const handleHeroUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    setHeroUploading(true);
    try {
      const formData = new FormData();
      formData.append('image', file);
      formData.append('user_type', selectedHeroType);

      await api.post('/cms/hero-image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.success('Hero image uploaded successfully!');
      fetchHeroImages();
    } catch (err) {
      console.error('Error uploading hero image:', err);
      toast.error(err.response?.data?.message || 'Failed to upload hero image');
    } finally {
      setHeroUploading(false);
      if (heroFileRef.current) heroFileRef.current.value = '';
    }
  };

  const handleDeleteHero = async (type) => {
    if (!window.confirm('Are you sure you want to delete this hero image?')) return;
    
    const pageId = type === 'office' ? 'mobile-office' : type === 'driver' ? 'mobile-driver' : 'mobile-app';
    try {
      await api.delete(`/cms/heroes/${pageId}`);
      toast.success('Hero image deleted');
      fetchHeroImages();
    } catch (err) {
      toast.error('Failed to delete hero image');
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    setLogoUploading(true);
    try {
      const formData = new FormData();
      formData.append('image', file);

      await api.post(`/cms/mobile-logo/${selectedLogoType}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.success('Logo uploaded successfully!');
      fetchLogos();
    } catch (err) {
      console.error('Error uploading logo:', err);
      toast.error(err.response?.data?.message || 'Failed to upload logo');
    } finally {
      setLogoUploading(false);
      if (logoFileRef.current) logoFileRef.current.value = '';
    }
  };

  const handleDeleteLogo = async (type) => {
    if (!window.confirm('Are you sure you want to delete this logo?')) return;
    
    try {
      await api.delete(`/cms/mobile-logo/${type}`);
      toast.success('Logo deleted');
      fetchLogos();
    } catch (err) {
      toast.error('Failed to delete logo');
    }
  };

  const handleUpload = async () => {
    if (!uploadForm.apk) {
      toast.error('Please select an APK file');
      return;
    }

    if (!uploadForm.version_name || !uploadForm.version_code) {
      toast.error('Please fill in version name and code');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('version_name', uploadForm.version_name);
      formData.append('version_code', uploadForm.version_code);
      formData.append('release_notes', uploadForm.release_notes);
      formData.append('is_required', uploadForm.is_required);
      formData.append('apk', uploadForm.apk);

      await api.post('/app/versions', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.success('App version uploaded successfully!');
      setShowUploadModal(false);
      setUploadForm({ version_name: '', version_code: '', release_notes: '', is_required: false, apk: null });
      fetchVersions();
    } catch (err) {
      console.error('Error uploading version:', err);
      toast.error(err.response?.data?.message || 'Failed to upload app version');
    } finally {
      setUploading(false);
    }
  };

  const closeModal = () => {
    setShowUploadModal(false);
    setUploadForm({ version_name: '', version_code: '', release_notes: '', is_required: false, apk: null });
  };

  const handleFileSelect = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleToggleActive = async (version) => {
    try {
      await api.put(`/app/versions/${version.id}`, {
        is_active: !version.is_active
      });
      toast.success(version.is_active ? 'Version deactivated' : 'Version activated');
      fetchVersions();
    } catch (err) {
      toast.error('Failed to update version');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this version?')) return;
    try {
      await api.delete(`/app/versions/${id}`);
      toast.success('Version deleted');
      fetchVersions();
    } catch (err) {
      toast.error('Failed to delete version');
    }
  };

  const formatDate = (date) => new Date(date).toLocaleDateString('en-US', {
    year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
  });

  const latestVersion = versions.find(v => v.is_active);
  const downloadUrl = latestVersion?.apk_url;

  return (
    <div className="page-container">
      <div style={{ marginBottom: '24px' }}>
        <h1 style={{ margin: '0 0 4px', fontSize: '28px', fontWeight: '700', color: '#FFFFFF' }}>📱 App Management</h1>
        <p style={{ color: '#A0A0A0', margin: 0, fontSize: '14px' }}>Manage GVC Driver App versions, hero images, logos, and distribution</p>
      </div>

      {/* Quick Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '24px' }}>
        <div style={{ background: '#1E1E1E', padding: '20px', borderRadius: '12px', border: '2px solid #2A2A2A' }}>
          <div style={{ fontSize: '14px', color: '#A0A0A0', marginBottom: '4px' }}>Current Version</div>
          <div style={{ fontSize: '24px', fontWeight: '700', color: '#10B981' }}>
            {latestVersion?.version_name || 'None'}
          </div>
        </div>
        <div style={{ background: '#1E1E1E', padding: '20px', borderRadius: '12px', border: '2px solid #2A2A2A' }}>
          <div style={{ fontSize: '14px', color: '#A0A0A0', marginBottom: '4px' }}>Total Downloads</div>
          <div style={{ fontSize: '24px', fontWeight: '700', color: '#3B82F6' }}>
            {versions.reduce((sum, v) => sum + (v.download_count || 0), 0)}
          </div>
        </div>
        <div style={{ background: '#1E1E1E', padding: '20px', borderRadius: '12px', border: '2px solid #2A2A2A' }}>
          <div style={{ fontSize: '14px', color: '#A0A0A0', marginBottom: '4px' }}>Total Versions</div>
          <div style={{ fontSize: '24px', fontWeight: '700', color: '#8B5CF6' }}>
            {versions.length}
          </div>
        </div>
      </div>

      {/* Mobile App Logos Section */}
      <div style={{ background: '#1E1E1E', padding: '24px', borderRadius: '12px', border: '2px solid #2A2A2A', marginBottom: '24px' }}>
        <h2 style={{ margin: '0 0 16px', fontSize: '18px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '8px', color: '#FFFFFF' }}>
          🎨 Mobile App Logos
        </h2>
        <p style={{ color: '#A0A0A0', fontSize: '14px', marginBottom: '20px' }}>
          Upload logos for the mobile app. <strong>Header Logo</strong> appears in Calendar and other headers (60px). <strong>Footer Logo</strong> appears at bottom of Home screen (35px, transparent recommended).
        </p>

        {/* Logo Type Selector */}
        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', fontSize: '14px', color: '#E0E0E0' }}>
            Upload for:
          </label>
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            {[
              { value: 'header', label: '📋 Header Logo', desc: 'Calendar, page headers (60px)' },
              { value: 'footer', label: '🦶 Footer Logo', desc: 'Home screen bottom (35px, transparent)' }
            ].map(opt => (
              <button
                key={opt.value}
                onClick={() => setSelectedLogoType(opt.value)}
                style={{
                  padding: '12px 16px',
                  borderRadius: '8px',
                  border: selectedLogoType === opt.value ? '2px solid #D4A94E' : '2px solid #2A2A2A',
                  background: selectedLogoType === opt.value ? 'rgba(212,169,78,0.1)' : '#1E1E1E',
                  cursor: 'pointer',
                  textAlign: 'left',
                  color: '#FFFFFF'
                }}
              >
                <div style={{ fontWeight: '600', fontSize: '14px', color: '#FFFFFF' }}>{opt.label}</div>
                <div style={{ fontSize: '12px', color: '#A0A0A0' }}>{opt.desc}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Upload Area */}
        <div style={{ marginBottom: '20px' }}>
          <input
            ref={logoFileRef}
            type="file"
            accept="image/*"
            onChange={handleLogoUpload}
            style={{ display: 'none' }}
          />
          <button
            onClick={() => logoFileRef.current?.click()}
            disabled={logoUploading}
            style={{
              width: '100%',
              padding: '30px',
              border: '2px dashed #2A2A2A',
              borderRadius: '12px',
              background: '#141414',
              cursor: logoUploading ? 'wait' : 'pointer',
              textAlign: 'center',
              color: '#FFFFFF'
            }}
          >
            {logoUploading ? (
              <div>
                <span style={{ fontSize: '32px' }}>⏳</span>
                <p style={{ margin: '8px 0 0', fontWeight: '500', color: '#FFFFFF' }}>Uploading...</p>
              </div>
            ) : (
              <div>
                <span style={{ fontSize: '32px' }}>🎨</span>
                <p style={{ margin: '8px 0 0', fontWeight: '500', color: '#FFFFFF' }}>Click to upload {selectedLogoType} logo</p>
                <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#A0A0A0' }}>
                  {selectedLogoType === 'footer' ? 'PNG with transparent background recommended' : 'PNG or JPG, any size'}
                </p>
              </div>
            )}
          </button>
        </div>

        {/* Current Logos */}
        {logoLoading ? (
          <div style={{ textAlign: 'center', padding: '20px', color: '#A0A0A0' }}>Loading logos...</div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '16px' }}>
            {[
              { key: 'header', label: '📋 Header Logo', desc: 'Calendar & page headers' },
              { key: 'footer', label: '🦶 Footer Logo', desc: 'Home screen bottom' }
            ].map(item => (
              <div key={item.key} style={{ border: '1px solid #2A2A2A', borderRadius: '8px', overflow: 'hidden' }}>
                <div style={{ padding: '12px', background: '#1A1A1A', borderBottom: '1px solid #2A2A2A', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <span style={{ fontWeight: '500', fontSize: '14px', color: '#FFFFFF' }}>{item.label}</span>
                    <div style={{ fontSize: '12px', color: '#A0A0A0' }}>{item.desc}</div>
                  </div>
                  {logos[item.key] && (
                    <button
                      onClick={() => handleDeleteLogo(item.key)}
                      style={{ padding: '4px 8px', fontSize: '12px', background: 'rgba(239,68,68,0.1)', color: '#F87171', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                    >
                      🗑️ Delete
                    </button>
                  )}
                </div>
                <div style={{ height: '80px', background: item.key === 'footer' ? '#141414' : '#000', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '10px' }}>
                  {logos[item.key] ? (
                    <img
                      src={logos[item.key]}
                      alt={item.label}
                      style={{ maxHeight: '100%', maxWidth: '100%', objectFit: 'contain' }}
                    />
                  ) : (
                    <span style={{ color: '#6B6B6B', fontSize: '14px' }}>No logo set (using default)</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Mobile Hero Image Section */}
      <div style={{ background: '#1E1E1E', padding: '24px', borderRadius: '12px', border: '2px solid #2A2A2A', marginBottom: '24px' }}>
        <h2 style={{ margin: '0 0 16px', fontSize: '18px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '8px', color: '#FFFFFF' }}>
          🖼️ Mobile App Hero Image
        </h2>
        <p style={{ color: '#A0A0A0', fontSize: '14px', marginBottom: '20px' }}>
          Upload a banner image that appears at the top of the mobile app home screen. You can set different images for office staff vs drivers.
        </p>

        {/* Hero Type Selector */}
        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', fontSize: '14px', color: '#E0E0E0' }}>
            Upload for:
          </label>
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            {[
              { value: 'all', label: '🌐 All Users', desc: 'Shows for everyone' },
              { value: 'office', label: '🏢 Office Staff', desc: 'Admin, HR, Managers' },
              { value: 'driver', label: '🚌 Drivers', desc: 'Driver accounts only' }
            ].map(opt => (
              <button
                key={opt.value}
                onClick={() => setSelectedHeroType(opt.value)}
                style={{
                  padding: '12px 16px',
                  borderRadius: '8px',
                  border: selectedHeroType === opt.value ? '2px solid #D4A94E' : '2px solid #2A2A2A',
                  background: selectedHeroType === opt.value ? 'rgba(212,169,78,0.1)' : '#1E1E1E',
                  cursor: 'pointer',
                  textAlign: 'left',
                  color: '#FFFFFF'
                }}
              >
                <div style={{ fontWeight: '600', fontSize: '14px', color: '#FFFFFF' }}>{opt.label}</div>
                <div style={{ fontSize: '12px', color: '#A0A0A0' }}>{opt.desc}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Upload Area */}
        <div style={{ marginBottom: '20px' }}>
          <input
            ref={heroFileRef}
            type="file"
            accept="image/*"
            onChange={handleHeroUpload}
            style={{ display: 'none' }}
          />
          <button
            onClick={() => heroFileRef.current?.click()}
            disabled={heroUploading}
            style={{
              width: '100%',
              padding: '30px',
              border: '2px dashed #2A2A2A',
              borderRadius: '12px',
              background: '#141414',
              cursor: heroUploading ? 'wait' : 'pointer',
              textAlign: 'center',
              color: '#FFFFFF'
            }}
          >
            {heroUploading ? (
              <div>
                <span style={{ fontSize: '32px' }}>⏳</span>
                <p style={{ margin: '8px 0 0', fontWeight: '500', color: '#FFFFFF' }}>Uploading...</p>
              </div>
            ) : (
              <div>
                <span style={{ fontSize: '32px' }}>📷</span>
                <p style={{ margin: '8px 0 0', fontWeight: '500', color: '#FFFFFF' }}>Click to upload hero image</p>
                <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#A0A0A0' }}>
                  Recommended: 1200x400 pixels, JPG or PNG
                </p>
              </div>
            )}
          </button>
        </div>

        {/* Current Hero Images */}
        {heroLoading ? (
          <div style={{ textAlign: 'center', padding: '20px', color: '#A0A0A0' }}>Loading hero images...</div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '16px' }}>
            {[
              { key: 'all', label: '🌐 All Users', pageId: 'mobile-app' },
              { key: 'office', label: '🏢 Office Staff', pageId: 'mobile-office' },
              { key: 'driver', label: '🚌 Drivers', pageId: 'mobile-driver' }
            ].map(item => (
              <div key={item.key} style={{ border: '1px solid #2A2A2A', borderRadius: '8px', overflow: 'hidden' }}>
                <div style={{ padding: '12px', background: '#1A1A1A', borderBottom: '1px solid #2A2A2A', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontWeight: '500', fontSize: '14px', color: '#FFFFFF' }}>{item.label}</span>
                  {heroImages[item.key] && (
                    <button
                      onClick={() => handleDeleteHero(item.key)}
                      style={{ padding: '4px 8px', fontSize: '12px', background: 'rgba(239,68,68,0.1)', color: '#F87171', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                    >
                      🗑️ Delete
                    </button>
                  )}
                </div>
                <div style={{ height: '120px', background: '#000', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {heroImages[item.key] ? (
                    <img
                      src={heroImages[item.key]}
                      alt={item.label}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  ) : (
                    <span style={{ color: '#6B6B6B', fontSize: '14px' }}>No image set</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Download Link Card */}
      {latestVersion && (
        <div style={{ background: 'linear-gradient(135deg, rgba(212,169,78,0.1) 0%, rgba(184,150,62,0.15) 100%)', padding: '20px', borderRadius: '12px', marginBottom: '24px', border: '2px solid #D4A94E' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
            <div>
              <h3 style={{ margin: '0 0 8px', fontSize: '18px', fontWeight: '600', color: '#FFFFFF' }}>🔗 Download Link for Employees</h3>
              <p style={{ margin: 0, fontSize: '14px', color: '#D4A94E' }}>
                Share this link with drivers to download the app:
              </p>
              <code style={{ display: 'block', marginTop: '8px', padding: '8px 12px', background: 'rgba(0,0,0,0.3)', borderRadius: '6px', fontSize: '14px', wordBreak: 'break-all', color: '#E0E0E0' }}>
                {downloadUrl || 'No active version available'}
              </code>
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                onClick={() => navigator.clipboard.writeText(downloadUrl).then(() => toast.success('Link copied!'))}
                style={{ padding: '10px 20px', background: '#1E1E1E', border: '2px solid #D4A94E', borderRadius: '8px', fontWeight: '600', cursor: 'pointer', color: '#D4A94E' }}
              >
                📋 Copy Link
              </button>
              <a
                href={downloadUrl}
                download
                style={{ padding: '10px 20px', background: '#D4A94E', border: 'none', borderRadius: '8px', fontWeight: '600', cursor: 'pointer', textDecoration: 'none', color: '#000' }}
              >
                ⬇️ Download APK
              </a>
            </div>
          </div>
        </div>
      )}

      {/* Upload Button */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '20px' }}>
        <button
          onClick={() => setShowUploadModal(true)}
          style={{ padding: '12px 24px', background: '#D4A94E', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', color: '#000' }}
        >
          📤 Upload New Version
        </button>
      </div>

      {/* Versions List */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#A0A0A0' }}>Loading...</div>
      ) : versions.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 20px', background: '#1E1E1E', borderRadius: '12px' }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>📱</div>
          <h3 style={{ margin: '0 0 8px', color: '#FFFFFF' }}>No App Versions Yet</h3>
          <p style={{ color: '#A0A0A0', marginBottom: '20px' }}>Upload your first APK to get started</p>
          <button
            onClick={() => setShowUploadModal(true)}
            style={{ padding: '12px 24px', background: '#D4A94E', border: 'none', borderRadius: '8px', fontWeight: '600', cursor: 'pointer', color: '#000' }}
          >
            Upload First Version
          </button>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {versions.map((version) => (
            <div
              key={version.id}
              style={{
                background: '#1E1E1E',
                borderRadius: '12px',
                padding: '20px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                border: version.is_active ? '2px solid #10B981' : '2px solid #2A2A2A',
                opacity: version.is_active ? 1 : 0.7
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '16px' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                    <span style={{ fontSize: '24px' }}>📦</span>
                    <div>
                      <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600', color: '#FFFFFF' }}>
                        Version {version.version_name}
                        <span style={{ marginLeft: '8px', fontSize: '12px', color: '#A0A0A0', fontWeight: '400' }}>
                          (Code: {version.version_code})
                        </span>
                      </h3>
                      <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#A0A0A0' }}>
                        Uploaded {formatDate(version.created_at)}
                      </p>
                    </div>
                  </div>
                  
                  {version.release_notes && (
                    <p style={{ margin: '12px 0 0', fontSize: '14px', color: '#E0E0E0', background: '#141414', padding: '10px', borderRadius: '6px' }}>
                      {version.release_notes}
                    </p>
                  )}

                  <div style={{ display: 'flex', gap: '16px', marginTop: '12px', fontSize: '13px', color: '#A0A0A0' }}>
                    <span>⬇️ {version.download_count || 0} downloads</span>
                    {version.is_required && <span style={{ color: '#F87171' }}>⚠️ Required Update</span>}
                    {version.is_active && <span style={{ color: '#10B981' }}>✅ Active</span>}
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  <a
                    href={version.apk_url}
                    download
                    style={{ padding: '8px 16px', background: '#2A2A2A', border: 'none', borderRadius: '6px', fontSize: '13px', textDecoration: 'none', color: '#E0E0E0', fontWeight: '500' }}
                  >
                    ⬇️ Download
                  </a>
                  <button
                    onClick={() => handleToggleActive(version)}
                    style={{
                      padding: '8px 16px',
                      background: version.is_active ? 'rgba(239,68,68,0.1)' : 'rgba(34,197,94,0.1)',
                      border: 'none',
                      borderRadius: '6px',
                      fontSize: '13px',
                      cursor: 'pointer',
                      fontWeight: '500',
                      color: version.is_active ? '#F87171' : '#4ADE80'
                    }}
                  >
                    {version.is_active ? 'Deactivate' : 'Activate'}
                  </button>
                  <button
                    onClick={() => handleDelete(version.id)}
                    style={{ padding: '8px 16px', background: 'rgba(239,68,68,0.1)', color: '#F87171', border: 'none', borderRadius: '6px', fontSize: '13px', cursor: 'pointer', fontWeight: '500' }}
                  >
                    🗑️ Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Upload Modal */}
      {showUploadModal && (
        <div 
          style={{ 
            position: 'fixed', 
            top: 0, 
            left: 0, 
            right: 0, 
            bottom: 0, 
            background: 'rgba(0,0,0,0.7)',
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            zIndex: 1000 
          }}
          onClick={closeModal}
        >
          <div 
            style={{ 
              background: '#1E1E1E',
              borderRadius: '16px',
              padding: '24px',
              width: '500px',
              maxWidth: '90%',
              maxHeight: '90vh',
              overflow: 'auto',
              color: '#FFFFFF'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{ margin: '0 0 20px', fontSize: '20px', fontWeight: '600', color: '#FFFFFF' }}>📤 Upload New App Version</h2>
            
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '14px', color: '#E0E0E0' }}>
                Version Name * <span style={{ color: '#A0A0A0', fontWeight: '400' }}>(e.g., 1.0.1)</span>
              </label>
              <input
                type="text"
                value={uploadForm.version_name}
                onChange={(e) => setUploadForm({ ...uploadForm, version_name: e.target.value })}
                placeholder="1.0.1"
                style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #2A2A2A', fontSize: '14px', boxSizing: 'border-box', background: '#141414', color: 'white' }}
              />
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '14px', color: '#E0E0E0' }}>
                Version Code * <span style={{ color: '#A0A0A0', fontWeight: '400' }}>(integer, e.g., 2)</span>
              </label>
              <input
                type="number"
                value={uploadForm.version_code}
                onChange={(e) => setUploadForm({ ...uploadForm, version_code: e.target.value })}
                placeholder="2"
                min="1"
                style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #2A2A2A', fontSize: '14px', boxSizing: 'border-box', background: '#141414', color: 'white' }}
              />
              <p style={{ margin: '4px 0 0', fontSize: '12px', color: '#A0A0A0' }}>
                Must be higher than previous version code for updates to work
              </p>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '14px', color: '#E0E0E0' }}>Release Notes</label>
              <textarea
                value={uploadForm.release_notes}
                onChange={(e) => setUploadForm({ ...uploadForm, release_notes: e.target.value })}
                placeholder="What's new in this version..."
                rows={3}
                style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #2A2A2A', fontSize: '14px', resize: 'vertical', boxSizing: 'border-box', background: '#141414', color: 'white' }}
              />
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '14px', color: '#E0E0E0' }}>APK File *</label>
              <input
                ref={fileInputRef}
                type="file"
                accept=".apk"
                onChange={(e) => {
                  if (e.target.files && e.target.files[0]) {
                    setUploadForm({ ...uploadForm, apk: e.target.files[0] });
                  }
                }}
                style={{ display: 'none' }}
              />
              <button
                type="button"
                onClick={handleFileSelect}
                style={{
                  width: '100%',
                  border: '2px dashed #2A2A2A',
                  borderRadius: '8px',
                  padding: '20px',
                  textAlign: 'center',
                  background: '#141414',
                  cursor: 'pointer',
                  color: '#FFFFFF'
                }}
              >
                {uploadForm.apk ? (
                  <div>
                    <span style={{ fontSize: '24px' }}>📦</span>
                    <p style={{ margin: '8px 0 0', fontWeight: '500', color: '#FFFFFF' }}>{uploadForm.apk.name}</p>
                    <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#A0A0A0' }}>
                      {(uploadForm.apk.size / (1024 * 1024)).toFixed(2)} MB
                    </p>
                  </div>
                ) : (
                  <div>
                    <span style={{ fontSize: '32px' }}>📱</span>
                    <p style={{ margin: '8px 0' }}>Click to select APK file</p>
                  </div>
                )}
              </button>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={uploadForm.is_required}
                  onChange={(e) => setUploadForm({ ...uploadForm, is_required: e.target.checked })}
                  style={{ width: '18px', height: '18px' }}
                />
                <span style={{ fontWeight: '500', color: '#FFFFFF' }}>Required Update</span>
                <span style={{ fontSize: '13px', color: '#A0A0A0' }}>(Forces users to update)</span>
              </label>
            </div>

            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                type="button"
                onClick={closeModal}
                style={{
                  flex: 1,
                  padding: '12px',
                  background: '#2A2A2A',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  color: '#E0E0E0'
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleUpload}
                disabled={uploading || !uploadForm.apk || !uploadForm.version_name || !uploadForm.version_code}
                style={{
                  flex: 1,
                  padding: '12px',
                  background: (uploading || !uploadForm.apk || !uploadForm.version_name || !uploadForm.version_code) ? '#2A2A2A' : '#D4A94E',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: uploading ? 'wait' : 'pointer',
                  color: (uploading || !uploadForm.apk || !uploadForm.version_name || !uploadForm.version_code) ? '#6B6B6B' : '#000'
                }}
              >
                {uploading ? '⏳ Uploading...' : '📤 Upload'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AppManagement;
